import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useBranding } from "../useBranding";
import { Avatar } from "./Atoms";
import {
  House, Users, Briefcase, Megaphone, ClipboardText, Stack, CurrencyDollarSimple,
  ChartLineUp, Gear, Trophy, UserCircle, IdentificationBadge, SignOut, List, Sparkle, WarningCircle, Receipt,
  Buildings, Palette, Envelope,
} from "@phosphor-icons/react";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Overview", icon: House, end: true },
    { to: "/admin/clients", label: "Clients", icon: Briefcase },
    { to: "/admin/ambassadors", label: "Ambassadors", icon: Users },
    { to: "/admin/projects", label: "Projects", icon: Stack },
    { to: "/admin/applications", label: "Apps", icon: ClipboardText },
    { to: "/admin/submissions", label: "Subs", icon: Megaphone },
    { to: "/admin/verifications", label: "Verify", icon: IdentificationBadge },
    { to: "/admin/organizations", label: "Tenants", icon: Buildings },
    { to: "/admin/leads", label: "Leads", icon: Envelope },
    { to: "/admin/payouts", label: "Payouts", icon: CurrencyDollarSimple },
    { to: "/admin/reports", label: "Reports", icon: ChartLineUp },
    { to: "/admin/settings", label: "Settings", icon: Gear },
  ],
  client: [
    { to: "/client", label: "Overview", icon: House, end: true },
    { to: "/client/projects", label: "Projects", icon: Stack },
    { to: "/client/ambassadors", label: "Creators", icon: Users },
    { to: "/client/communicate", label: "Communicate", icon: Megaphone },
    { to: "/client/reports", label: "Reports", icon: ChartLineUp },
    { to: "/client/branding", label: "Branding", icon: Palette },
    { to: "/client/issues", label: "Issues", icon: WarningCircle },
  ],
  ambassador: [
    { to: "/me", label: "Home", icon: House, end: true },
    { to: "/me/projects", label: "Projects", icon: Stack },
    { to: "/me/updates", label: "Updates", icon: Megaphone },
    { to: "/me/payouts", label: "Earnings", icon: Receipt },
    { to: "/me/leaderboard", label: "Leaderboard", icon: Trophy },
    { to: "/me/profile", label: "Profile", icon: UserCircle },
  ],
};

const ROLE_BADGE = {
  admin: { label: "Admin", color: "#4D7C0F" },
  client: { label: "Brand", color: "#1D4ED8" },
  ambassador: { label: "Creator", color: "#BE185D" },
};

/** Convert `#RRGGBB` → `"r, g, b"` string for use inside rgba() with CSS vars.
 *  Safe fallback returns the CrewHQ steel-blue default. */
function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return "59, 130, 246";
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return "59, 130, 246";
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Live white-labeling: fetch the tenant's branding by their organization slug
  // (present on the logged-in user payload). Falls back to default when absent.
  const branding = useBranding(user?.organization_slug || null);
  if (!user) return null;
  const items = NAV_BY_ROLE[user.role] || [];
  const badge = ROLE_BADGE[user.role];

  const accent = branding?.accent_color || "#3B82F6";
  const secondary = branding?.secondary_color || "#93C5FD";
  const appName = branding?.app_name || "CrewHQ";
  const logoUrl = branding?.logo_url || "";
  // CrewHQ is our default — split "HQ" onto the accent color. For a real
  // tenant, render the app_name plain (no "HQ" suffix magic).
  const renderAppName = () => (
    appName.endsWith("HQ") && appName.includes("Crew")
      ? (<>{appName.replace("HQ", "")}<span style={{ color: accent }}>HQ</span></>)
      : (<span>{appName}</span>)
  );

  const handleLogout = async () => { await logout(); nav("/login"); };

  // Mobile bottom nav uses primary 4-5 items
  const bottomItems = items.slice(0, 5);

  const logoTile = (big) => (
    logoUrl ? (
      <img src={logoUrl} alt={appName}
           className={big ? "w-10 h-10 rounded-2xl object-cover" : "w-8 h-8 rounded-xl object-cover"} />
    ) : (
      <div className={`${big ? "w-10 h-10 rounded-2xl" : "w-8 h-8 rounded-xl"} grid place-items-center`}
           style={{ background: `linear-gradient(135deg, ${accent} 0%, ${secondary} 100%)`,
                    boxShadow: `0 0 24px ${accent}44` }}>
        <Sparkle size={big ? 20 : 16} weight="fill" color="white" />
      </div>
    )
  );

  return (
    <div className="app-bg flex" style={{
      "--brand-accent": accent,
      "--brand-secondary": secondary,
      "--brand-accent-rgb": hexToRgb(accent),
      "--brand-secondary-rgb": hexToRgb(secondary),
    }}>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 min-h-screen p-5 sticky top-0">
        <div className="flex items-center gap-2 mb-8">
          {logoTile(true)}
          <div>
            <div className="text-white font-extrabold tracking-tight leading-none" data-testid="sidebar-app-name">{renderAppName()}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted font-bold">Creator Control</div>
          </div>
        </div>
        <nav className="flex-1 flex flex-col gap-1">
          {items.map(it => (
            <NavLink key={it.to} to={it.to} end={it.end}
              data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition
                 ${isActive ? "nav-active" : "text-muted hover:text-white hover:bg-white/5"}`}>
              <it.icon size={18} weight="bold" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="glass-soft p-3 mt-3">
          <div className="flex items-center gap-3">
            <Avatar name={(user.role === "client" && user.company) || user.name} size={36} />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate" data-testid="sidebar-user-name">{(user.role === "client" && user.company) || user.name}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: badge.color }}>{badge.label}</div>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="text-muted hover:text-white">
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        {/* Top mobile header */}
        <header className="lg:hidden sticky top-0 z-40 glass-soft rounded-none border-x-0 border-t-0 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {logoTile(false)}
            <div className="text-white font-extrabold tracking-tight" data-testid="mobile-app-name">{renderAppName()}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="pill" style={{ color: badge.color, background: `${badge.color}1a`, borderColor: `${badge.color}55` }}>{badge.label}</span>
            <button onClick={() => setMobileOpen(true)} className="w-8 h-8 grid place-items-center rounded-xl bg-white/5 border border-white/10" data-testid="mobile-menu-btn">
              <List size={16} weight="bold" color="white" />
            </button>
          </div>
        </header>

        <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-bottom-nav">
          {children}
        </div>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 glass rounded-none border-x-0 border-b-0 border-t border-white/10 px-3 py-2 flex items-center justify-around" style={{backdropFilter:'blur(24px)'}}>
          {bottomItems.map(it => (
            <NavLink key={it.to} to={it.to} end={it.end}
              data-testid={`mobile-nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl ${isActive ? "text-white" : "text-muted"}`}>
              {({ isActive }) => (
                <>
                  <div className="w-10 h-10 grid place-items-center rounded-xl transition"
                       style={isActive ? {
                         background: `linear-gradient(135deg, ${accent} 0%, ${secondary} 100%)`,
                         boxShadow: `0 0 20px ${accent}44`,
                       } : {}}>
                    <it.icon size={18} weight={isActive ? "fill" : "bold"} color={isActive ? "white" : undefined} />
                  </div>
                  <span className="text-[10px] font-bold tracking-wide">{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Mobile slide-over menu (more options + logout) */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-[60]" onClick={() => setMobileOpen(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="absolute right-0 top-0 bottom-0 w-72 glass p-5 flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-6">
                <Avatar name={(user.role === "client" && user.company) || user.name} />
                <div>
                  <div className="text-white font-bold">{(user.role === "client" && user.company) || user.name}</div>
                  <div className="text-xs" style={{ color: badge.color }}>{badge.label}</div>
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                {items.map(it => (
                  <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold ${isActive ? "bg-white/8 text-white" : "text-muted hover:text-white"}`}>
                    <it.icon size={18} weight="bold" /> {it.label}
                  </NavLink>
                ))}
              </div>
              <button onClick={handleLogout} className="btn-ghost flex items-center justify-center gap-2 mt-3" data-testid="mobile-logout-btn">
                <SignOut size={16} weight="bold" /> Sign out
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
